import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Division, JobTitle } from '@connect-x-app/shared-domain-models';
import i18n from '@connect-x-app/shared-translations';
import { Option } from '@connect-x-app/shared-types';
import { FormValidator, getHoursFromSeconds, UserUtil, ValidatorResponse } from '@connect-x-app/shared-utils';
import {
  ALL_SYSTEM_MESSAGES_FUNCTIONS,
  FunctionListItem,
  ReceiverOptions,
  SubmitSystemMessageForm,
  SystemMessageForm,
  SystemMessageWithType,
} from '@connect-x-app/shared-view-models';
import { FormValidationErrors, useForm } from '@connect-x-app/web-state';
import { ADMIN_TEST_IDS } from '@connect-x-app/web-test-ids';
import { InputField, RadioButtons, SearchableSelect } from '@connect-x-app/web-ui';

import { ModalForm } from '../../../../../roles/components';
import {
  determineSystemMessageReceiver,
  getMessageTypeTranslationKey,
  prepareSystemMessagesFormDataForSubmit,
} from '../../../../../shared/utils/systemMessages';

type Props = {
  closeModal: () => void;
  isLoading: boolean;
  onSubmit: (systemMessageFormData: SubmitSystemMessageForm | null) => void;
  systemMessage?: SystemMessageWithType;
};

const mapFunctionsListToOptions = (functionsToMap: JobTitle[]) =>
  functionsToMap.map((systemMessageFunction: JobTitle) => {
    const functionToDivision: Division = UserUtil.getDivision(systemMessageFunction);

    return {
      label: functionToDivision,
      value: systemMessageFunction,
    };
  });

const defaultFunctionsList: FunctionListItem[] = mapFunctionsListToOptions(ALL_SYSTEM_MESSAGES_FUNCTIONS);

const defaultFormValues: SystemMessageForm = {
  functions: [],
  receiverOption: ReceiverOptions.ALL_FUNCTIONS,
  timeBeforeInHours: '24',
};

const validateFunctions = (
  validator: FormValidator,
  functions: FunctionListItem[],
  receiverOption: ReceiverOptions,
  functionsTranslation: string,
): ValidatorResponse | undefined => {
  if (receiverOption && receiverOption === ReceiverOptions.SPECIFIC_FUNCTIONS) {
    const hasLengthValidation = validator.dropdownRequired(functions.length, functionsTranslation);

    if (!hasLengthValidation.isValid) {
      return hasLengthValidation;
    }
  }

  return undefined;
};

const validateReceiverOption = (validator: FormValidator, receiverOption: ReceiverOptions): ValidatorResponse | undefined => {
  const requiredValidation = validator.required(receiverOption);

  if (!requiredValidation.isValid) {
    return requiredValidation;
  }

  return undefined;
};

const validateTimeBefore = (
  validator: FormValidator,
  timeBeforeInHours: string,
  timeBeforeMinValue: number,
  timeBeforeMaxValue: number,
  generalErrorTranslation: string,
): ValidatorResponse | undefined => {
  const requiredValidation = validator.required(timeBeforeInHours);
  if (!requiredValidation.isValid) {
    return requiredValidation;
  }

  const validNumberValidation = validator.number(timeBeforeInHours);
  if (!validNumberValidation.isValid) {
    return { ...validNumberValidation, message: generalErrorTranslation };
  }

  const isIntegerValidation = validator.isInteger(`${timeBeforeInHours}`);
  if (!isIntegerValidation.isValid) {
    return { ...isIntegerValidation, message: generalErrorTranslation };
  }

  const minValueValidation = validator.minValue(+timeBeforeInHours, timeBeforeMinValue);
  if (!minValueValidation.isValid) {
    return { ...minValueValidation, message: generalErrorTranslation };
  }

  const maxValueValidation = validator.maxValue(+timeBeforeInHours, timeBeforeMaxValue);
  if (!maxValueValidation.isValid) {
    return { ...maxValueValidation, message: generalErrorTranslation };
  }

  return undefined;
};

const timeBeforeMinValue = 1;
const timeBeforeMaxValue = 24;

const validateForm = (
  formValue: SystemMessageForm,
  functionsTranslation: string,
  generalErrorTranslation: string,
): FormValidationErrors<SystemMessageForm> => {
  const validator = new FormValidator();

  return {
    functions: validateFunctions(validator, formValue?.functions ?? [], formValue?.receiverOption ?? '', functionsTranslation),
    receiverOption: validateReceiverOption(validator, formValue?.receiverOption ?? ''),
    timeBeforeInHours: validateTimeBefore(
      validator,
      formValue?.timeBeforeInHours?.toString() ?? '',
      timeBeforeMinValue,
      timeBeforeMaxValue,
      generalErrorTranslation,
    ),
  } as FormValidationErrors<SystemMessageForm>;
};

export const EditSystemMessageModal = ({ closeModal, isLoading, onSubmit, systemMessage }: Props) => {
  const { t } = useTranslation();

  let title: string = t('SYSTEM_MESSAGES.ACTIONS.UPDATE');

  if (systemMessage?.messageType) {
    const { translationKey } = getMessageTypeTranslationKey(systemMessage.messageType);

    title = translationKey ? t(translationKey) : systemMessage.messageType;
  }

  const radioButtonOptions = [
    {
      disabled: false,
      label: t('SYSTEM_MESSAGES.FORM.ALL_FUNCTIONS'),
      value: ReceiverOptions.ALL_FUNCTIONS,
    },
    {
      disabled: false,
      label: t('SYSTEM_MESSAGES.FORM.SPECIFIC_FUNCTIONS'),
      value: ReceiverOptions.SPECIFIC_FUNCTIONS,
    },
    {
      disabled: false,
      label: t('SYSTEM_MESSAGES.FORM.NO_FUNCTIONS'),
      value: ReceiverOptions.NO_FUNCTIONS,
    },
  ];

  const initialFormData = useMemo(() => {
    const initialData: SystemMessageForm = { ...defaultFormValues };

    if (systemMessage?.functions) {
      initialData.receiverOption = determineSystemMessageReceiver(systemMessage.functions);
      if (initialData.receiverOption === ReceiverOptions.SPECIFIC_FUNCTIONS) {
        initialData.functions = mapFunctionsListToOptions(systemMessage.functions);
      }
    }

    if (systemMessage?.settings?.timeBefore) {
      initialData.timeBeforeInHours = getHoursFromSeconds(systemMessage.settings.timeBefore).toString();
    }

    return initialData;
  }, [systemMessage]);

  const functionsTranslation = t('SYSTEM_MESSAGES.FORM.FUNCTION');
  const generalErrorTranslation = i18n.t('FORM_VALIDATION.NUMBER.INTEGER_WITH_MIN_AND_MAX_VALUE', {
    maxValue: timeBeforeMaxValue,
    minValue: timeBeforeMinValue,
  });

  const form = useForm<SystemMessageForm>({
    ignoreDirtyCheck: (values: SystemMessageForm) => {
      if (values.receiverOption !== ReceiverOptions.SPECIFIC_FUNCTIONS) {
        return ['functions'];
      }
      return [];
    },
    initialForm: initialFormData,
    submitForm: () => {
      const preparedDataForSubmit: SubmitSystemMessageForm | null = prepareSystemMessagesFormDataForSubmit(
        form.values,
        systemMessage,
      );

      onSubmit(preparedDataForSubmit);
    },
    validateForm: (values: SystemMessageForm) => validateForm(values, functionsTranslation, generalErrorTranslation),
  });

  return (
    <ModalForm
      disabled={!form.isDirty}
      headerTitle={title}
      isDirty={form.isDirty}
      isSubmitting={isLoading}
      onCancel={closeModal}
      onSubmit={form.submit}
      submitButtonText={t('SHARED.BUTTONS.SAVE')}
      testId={ADMIN_TEST_IDS.systemMessages.modal}
    >
      <InputField
        label={t('SYSTEM_MESSAGES.FORM.TIME_BEFORE')}
        name="timeBeforeInHours"
        onChange={form.setAttribute}
        required
        showInlineBottomError
        validation={form.validationErrors?.timeBeforeInHours}
        value={form.values?.timeBeforeInHours?.toString() ?? ''}
      />
      <RadioButtons
        label={t('SYSTEM_MESSAGES.FORM.RECEIVER')}
        name="receiverOption"
        onChange={form.setAttribute}
        options={radioButtonOptions}
        showInlineBottomError
        validation={form.validationErrors?.receiverOption}
        value={form.values?.receiverOption ?? ''}
      />
      <SearchableSelect
        filterOption={(option: Option, inputValue: string): boolean =>
          option.label.toLowerCase().includes(inputValue.toLowerCase())
        }
        isHidden={form.values.receiverOption !== ReceiverOptions.SPECIFIC_FUNCTIONS}
        isModal
        label=""
        name="functions"
        onChange={form.setAttribute}
        options={defaultFunctionsList}
        searchableSelectConfig={{
          customNoOptionsAvailable: t('SYSTEM_MESSAGES.FORM.NO_OPTIONS'),
          placeholder: t('SYSTEM_MESSAGES.FORM.SELECT_FUNCTIONS'),
        }}
        selectedOptions={form.values?.functions ?? []}
        showInlineBottomError
        validation={form.validationErrors?.functions as ValidatorResponse | undefined}
      />
    </ModalForm>
  );
};
